import React, { useState, useEffect, useRef } from 'react';
import './Marketplace.css';
import '../../components/Button/Button.css';

const Marketplace = () => {
  const [selectedModel, setSelectedModel] = useState('All models');
  const [selectedPriceRange, setSelectedPriceRange] = useState('All');
  const [activeFilter, setActiveFilter] = useState('All');
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [priceDropdownOpen, setPriceDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'grid' or 'table'
  const [sortBy, setSortBy] = useState(null); // 'hashrate', 'income', 'roi', 'price'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [likedAsics, setLikedAsics] = useState(new Set());
  const [compareAsics, setCompareAsics] = useState(new Set());
  
  const modelDropdownRef = useRef(null);
  const priceDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  const faqItems = [
    {
      id: 1,
      question: 'What is the Hosted ASICs Marketplace?',
      answer: 'The Hosted ASICs Marketplace is a platform where users can buy and sell working ASIC miners that are already hosted and mining. All devices are tested and verified before being listed.'
    },
    {
      id: 2,
      question: 'What is the difference between buying a new ASIC from FX-Pool and an ASIC from Hosted ASIC Marketplace?',
      answer: 'New ASICs from FX-Pool are brand new devices directly from the manufacturer. Marketplace ASICs are pre-owned devices that have been used but are still in good working condition. Marketplace ASICs are typically available at lower prices.'
    },
    {
      id: 3,
      question: 'Are Hosted Marketplace ASICs tested before being listed?',
      answer: 'Yes, all ASICs listed on the Marketplace are thoroughly tested to ensure they are in working condition and meet our quality standards before being made available for purchase.'
    },
    {
      id: 4,
      question: 'Do Hosted Marketplace ASICs come with a warranty?',
      answer: 'Marketplace ASICs come with a limited warranty. Please check the individual listing for specific warranty details and terms.'
    },
    {
      id: 5,
      question: 'How do I choose the right device from the Hosted ASICs Marketplace?',
      answer: 'Consider factors such as hashrate, power consumption, price, FX-Pool Score, and ROI. Use the filters and sorting options to find ASICs that match your requirements.'
    },
    {
      id: 6,
      question: 'Can I resell my ASIC after purchasing it?',
      answer: 'Yes, you can resell your ASIC on the Marketplace at any time. Simply list your device and set your price. The transfer process is instant and secure.'
    }
  ];

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) {
        setModelDropdownOpen(false);
      }
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(event.target)) {
        setPriceDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mock listing data
  const listings = [
    {
      id: 1,
      name: 'Antminer S21 XP',
      hashrate: '270 TH/s',
      status: 'sold',
      price: '$8 350',
      buyerAvatar: 'data:image/png;base64,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',
      sellerAvatar: 'data:image/png;base64,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'
    },
    {
      id: 2,
      name: 'Antminer S21 PRO',
      hashrate: '245 TH/s',
      status: 'purchased',
      price: '$4 800',
      buyerAvatar: 'data:image/png;base64,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',
      sellerAvatar: 'data:image/png;base64,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'
    },
    {
      id: 3,
      name: 'Antminer S21 PRO',
      hashrate: '234 TH/s',
      status: 'sold',
      price: '$3 900',
      buyerAvatar: 'data:image/png;base64,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',
      sellerAvatar: 'data:image/png;base64,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'
    }
  ];

  const filterTags = [
    'All',
    'Lowest Price in the Market',
    'Less Days in Work',
    'Lowest Hosting Fee',
    'Highest Mining Power',
    'Highest Monthly Rewards',
    'Still in Warranty',
    'Recently Added'
  ];

  const asicModels = [
    'All models',
    'Antminer S19 Pro 106TH/s',
    'Antminer S19 XP 141TH/s',
    'Antminer S19J Pro 100TH/s',
    'Antminer S19J Pro 104TH/s',
    'Antminer S19J Pro 110TH/s',
    'Antminer S19J Pro 120TH/s',
    'Antminer S19K Pro 110TH/s',
    'Antminer S19K Pro 115TH/s',
    'Antminer S19K Pro 120TH/s',
    'Antminer S19j XP 151TH/s',
    'Antminer S21 188TH/s',
    'Antminer S21 200TH/s',
    'Antminer S21 Pro 234TH/s',
    'Antminer S21 Pro 245TH/s',
    'Antminer S21 XP 270TH/s',
    'Antminer S21+ 225TH/s',
    'Antminer S21+ 235TH/s'
  ];

  const priceRanges = [
    'All',
    '$0 – $1,000',
    '$1,000 – $3,000',
    '$3,000 – $5,000',
    '$5,000+'
  ];

  const toggleModelDropdown = () => {
    setModelDropdownOpen(!modelDropdownOpen);
    setPriceDropdownOpen(false);
    setSortDropdownOpen(false);
  };

  const togglePriceDropdown = () => {
    setPriceDropdownOpen(!priceDropdownOpen);
    setModelDropdownOpen(false);
    setSortDropdownOpen(false);
  };

  const toggleSortDropdown = () => {
    setSortDropdownOpen(!sortDropdownOpen);
    setModelDropdownOpen(false);
    setPriceDropdownOpen(false);
  };

  // ASIC Table Data
  const asicTableData = [
    { id: 956, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '85.6', hashrate: '188 TH/s', hashrateNum: 188, power: '3300W', dailyIncome: '$9.776', dailyIncomeNum: 9.776, roi: '26.47%', roiNum: 26.47, price: '$3280', priceNum: 3280, viewing: 52, fullModelName: 'Antminer S21 188TH/s' },
    { id: 957, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '85.6', hashrate: '188 TH/s', hashrateNum: 188, power: '3300W', dailyIncome: '$9.776', dailyIncomeNum: 9.776, roi: '26.47%', roiNum: 26.47, price: '$3280', priceNum: 3280, viewing: 59, fullModelName: 'Antminer S21 188TH/s' },
    { id: 958, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '85.6', hashrate: '188 TH/s', hashrateNum: 188, power: '3300W', dailyIncome: '$9.776', dailyIncomeNum: 9.776, roi: '26.47%', roiNum: 26.47, price: '$3280', priceNum: 3280, viewing: 16, fullModelName: 'Antminer S21 188TH/s' },
    { id: 959, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '83.1', hashrate: '200 TH/s', hashrateNum: 200, power: '3520W', dailyIncome: '$10.4', dailyIncomeNum: 10.4, roi: '25.21%', roiNum: 25.21, price: '$3729', priceNum: 3729, viewing: 23, fullModelName: 'Antminer S21 200TH/s' },
    { id: 960, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '83.1', hashrate: '200 TH/s', hashrateNum: 200, power: '3520W', dailyIncome: '$10.4', dailyIncomeNum: 10.4, roi: '25.21%', roiNum: 25.21, price: '$3729', priceNum: 3729, viewing: 30, fullModelName: 'Antminer S21 200TH/s' },
    { id: 961, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '83.1', hashrate: '200 TH/s', hashrateNum: 200, power: '3520W', dailyIncome: '$10.4', dailyIncomeNum: 10.4, roi: '25.21%', roiNum: 25.21, price: '$3729', priceNum: 3729, viewing: 37, fullModelName: 'Antminer S21 200TH/s' },
    { id: 962, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '83.1', hashrate: '200 TH/s', hashrateNum: 200, power: '3520W', dailyIncome: '$10.4', dailyIncomeNum: 10.4, roi: '25.21%', roiNum: 25.21, price: '$3729', priceNum: 3729, viewing: 44, fullModelName: 'Antminer S21 200TH/s' },
    { id: 1081, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '98.2', hashrate: '188 TH/s', hashrateNum: 188, power: '3300W', dailyIncome: '$9.776', dailyIncomeNum: 9.776, roi: '33.08%', roiNum: 33.08, price: '$3350', priceNum: 3350, viewing: 27, fullModelName: 'Antminer S21 188TH/s' },
    { id: 1082, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '98.2', hashrate: '188 TH/s', hashrateNum: 188, power: '3300W', dailyIncome: '$9.776', dailyIncomeNum: 9.776, roi: '33.08%', roiNum: 33.08, price: '$3350', priceNum: 3350, viewing: 34, fullModelName: 'Antminer S21 188TH/s' },
    { id: 1221, modelName: 'S21', modelImage: 'https://files.ecos.am/images/6m3UD1dKdDO6v6QuyC4I8FNtwKIpStucvBOxqODd.png', ecosScore: '89.4', hashrate: '188 TH/s', hashrateNum: 188, power: '3300W', dailyIncome: '$9.776', dailyIncomeNum: 9.776, roi: '29.34%', roiNum: 29.34, price: '$3777', priceNum: 3777, viewing: 57, fullModelName: 'Antminer S21 188TH/s' },
  ];

  // Popular Picks Data
  const popularPicks = [
    { name: 'Antminer S19K Pro 110TH/s', count: 4 },
    { name: 'Antminer S21 188TH/s', count: 45 },
    { name: 'Antminer S21 200TH/s', count: 60 },
    { name: 'Antminer S19 XP 141TH/s', count: 16 },
    { name: 'Antminer S19J Pro 100TH/s', count: 1 },
    { name: 'Antminer S21 Pro 234TH/s', count: 40 },
    { name: 'Antminer S21 Pro 245TH/s', count: 49 },
    { name: 'Antminer S21 XP 270TH/s', count: 21 },
    { name: 'Antminer S19K Pro 120TH/s', count: 15 },
    { name: 'Antminer S19J Pro 120TH/s', count: 8 },
    { name: 'Antminer S19K Pro 115TH/s', count: 7 },
    { name: 'Antminer S19j XP 151TH/s', count: 5 },
    { name: 'Antminer S21+ 235TH/s', count: 7 },
    { name: 'Antminer S19 Pro 106TH/s', count: 4 },
    { name: 'Antminer S21+ 225TH/s', count: 38 },
    { name: 'Antminer S19J Pro 104TH/s', count: 3 },
    { name: 'Antminer S19J Pro 110TH/s', count: 1 },
  ];

  // Recent Deals Data
  const recentDeals = [
    { id: 1, name: 'Antminer S19K Pro 120TH/s', value: '$153.75', date: '2026-01-10 19:31', seller: 'FX-Pool User' },
    { id: 2, name: 'Antminer S21+ 235TH/s', value: '$1', date: '2026-01-09 10:19', seller: 'FX-Pool User' },
    { id: 3, name: 'Antminer S21+ 235TH/s', value: '$2234.5', date: '2026-01-06 15:03', seller: 'FX-Pool User' },
    { id: 4, name: 'Antminer S19K Pro 120TH/s', value: '$287', date: '2026-01-04 12:24', seller: 'FX-Pool User' },
    { id: 5, name: 'Antminer S19K Pro 120TH/s', value: '$256.25', date: '2026-01-04 11:35', seller: 'FX-Pool User' },
  ];

  // Handle view toggle
  const handleViewToggle = (mode) => {
    setViewMode(mode);
  };

  // Handle sorting
  const handleSort = (sortType) => {
    if (sortBy === sortType) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortType);
      setSortOrder('asc');
    }
    setSortDropdownOpen(false);
  };

  // Handle sort dropdown selection
  const handleSortSelection = (option) => {
    if (option === 'Price: Low to High') {
      setSortBy('price');
      setSortOrder('asc');
    } else if (option === 'Price: High to Low') {
      setSortBy('price');
      setSortOrder('desc');
    } else if (option === 'Hashrate: Low to High') {
      setSortBy('hashrate');
      setSortOrder('asc');
    } else if (option === 'Hashrate: High to Low') {
      setSortBy('hashrate');
      setSortOrder('desc');
    } else if (option === 'Recently Added') {
      setSortBy('recent');
      setSortOrder('desc');
    }
    setSortDropdownOpen(false);
  };

  // Handle like toggle
  const handleLike = (asicId) => {
    setLikedAsics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(asicId)) {
        newSet.delete(asicId);
      } else {
        newSet.add(asicId);
      }
      return newSet;
    });
  };

  // Handle compare toggle
  const handleCompare = (asicId) => {
    setCompareAsics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(asicId)) {
        newSet.delete(asicId);
      } else {
        newSet.add(asicId);
      }
      return newSet;
    });
  };

  // Handle Buy button click
  const handleBuy = (e, asicId) => {
    e.preventDefault();
    window.history.pushState({ page: 'marketplace' }, '', `/marketplace/${asicId}`);
    // In a real app, this would navigate to a detail page or payment page
    console.log('Buy clicked for ASIC:', asicId);
  };

  // Handle popular pick click
  const handlePopularPickClick = (pickName) => {
    setSelectedModel(pickName);
    setModelDropdownOpen(false);
  };

  // Filter and sort data
  const getFilteredAndSortedData = () => {
    let filtered = [...asicTableData];

    // Filter by model
    if (selectedModel !== 'All models') {
      filtered = filtered.filter(asic => asic.fullModelName === selectedModel);
    }

    // Filter by price range
    if (selectedPriceRange !== 'All') {
      if (selectedPriceRange === '$0 – $1,000') {
        filtered = filtered.filter(asic => asic.priceNum >= 0 && asic.priceNum <= 1000);
      } else if (selectedPriceRange === '$1,000 – $3,000') {
        filtered = filtered.filter(asic => asic.priceNum > 1000 && asic.priceNum <= 3000);
      } else if (selectedPriceRange === '$3,000 – $5,000') {
        filtered = filtered.filter(asic => asic.priceNum > 3000 && asic.priceNum <= 5000);
      } else if (selectedPriceRange === '$5,000+') {
        filtered = filtered.filter(asic => asic.priceNum > 5000);
      }
    }

    // Apply active filter tags
    if (activeFilter !== 'All') {
      if (activeFilter === 'Lowest Price in the Market') {
        filtered.sort((a, b) => a.priceNum - b.priceNum);
      } else if (activeFilter === 'Highest Mining Power') {
        filtered.sort((a, b) => b.hashrateNum - a.hashrateNum);
      } else if (activeFilter === 'Highest Monthly Rewards') {
        filtered.sort((a, b) => b.dailyIncomeNum - a.dailyIncomeNum);
      }
    }

    // Apply sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let comparison = 0;
        if (sortBy === 'hashrate') {
          comparison = a.hashrateNum - b.hashrateNum;
        } else if (sortBy === 'income') {
          comparison = a.dailyIncomeNum - b.dailyIncomeNum;
        } else if (sortBy === 'roi') {
          comparison = a.roiNum - b.roiNum;
        } else if (sortBy === 'price') {
          comparison = a.priceNum - b.priceNum;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  };

  const filteredAsics = getFilteredAndSortedData();

  return (
    <div className="used">
      <div className="used__header">
        {/* Marketplace Header Section */}
        <section className="marketplace-asic">
          <div className="marketplace-asic__header">
            <div className="marketplace-asic__title-block">
              <span className="marketplace-asic__title">Hosted ASICs Marketplace</span>
              <span className="marketplace-asic__new-tag">NEW</span>
            </div>
            <p className="marketplace-asic__highlight">
              Buy & Sell <span>Working ASICs</span> Instantly
            </p>
            <div className="marketplace-asic__info">
              250+ ASICs mining right now. Zero downtime trading
            </div>
            <div className="marketplace-asic__features">
              <div className="marketplace-asic__feature-block">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7.58333 1.16699L2.38785 7.40157C2.18438 7.64574 2.08264 7.76782 2.08109 7.87093C2.07974 7.96056 2.11968 8.04584 2.1894 8.10218C2.2696 8.16699 2.42852 8.16699 2.74635 8.16699H7L6.41667 12.8337L11.6122 6.59908C11.8156 6.35491 11.9174 6.23283 11.9189 6.12972C11.9203 6.04009 11.8803 5.95481 11.8106 5.89847C11.7304 5.83366 11.5715 5.83366 11.2536 5.83366H7L7.58333 1.16699Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                <span className="marketplace-asic__feature">Instant Transfer</span>
              </div>
              <div className="marketplace-asic__feature-block">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M11.6663 3.5L5.24967 9.91667L2.33301 7" stroke="white" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                <span className="marketplace-asic__feature">Hosted</span>
              </div>
              <div className="marketplace-asic__feature-block">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9.91634 5.83333V4.66667C9.91634 3.05584 8.61051 1.75 6.99967 1.75C5.38884 1.75 4.08301 3.05584 4.08301 4.66667V5.83333M6.99967 8.45833V9.625M5.13301 12.25H8.86634C9.84643 12.25 10.3365 12.25 10.7108 12.0593C11.0401 11.8915 11.3078 11.6238 11.4756 11.2945C11.6663 10.9201 11.6663 10.4301 11.6663 9.45V8.63333C11.6663 7.65324 11.6663 7.1632 11.4756 6.78885C11.3078 6.45957 11.0401 6.19185 10.7108 6.02407C10.3365 5.83333 9.84643 5.83333 8.86634 5.83333H5.13301C4.15292 5.83333 3.66287 5.83333 3.28852 6.02407C2.95924 6.19185 2.69152 6.45957 2.52375 6.78885C2.33301 7.1632 2.33301 7.65324 2.33301 8.63333V9.45C2.33301 10.4301 2.33301 10.9201 2.52375 11.2945C2.69152 11.6238 2.95924 11.8915 3.28852 12.0593C3.66287 12.25 4.15292 12.25 5.13301 12.25Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                <span className="marketplace-asic__feature">Secure Deals</span>
              </div>
            </div>
          </div>

          {/* Latest Listings Section */}
          <div className="marketplace-asic__listings">
            <div className="marketplace-asic__listings-title-block">
              <h3>Latest Listings</h3>
              <p>
                <span></span>Live
              </p>
            </div>
            <div className="marketplace-asic__listing-item-group">
              {listings.map((listing) => (
                <div key={listing.id} className="marketplace-asic__listing-item">
                  <div className="marketplace-asic__listing-item-block">
                    <span className="marketplace-asic__item-name">{listing.name}</span>
                    <span className="marketplace-asic__hashrate">{listing.hashrate}</span>
                  </div>
                  <div className="marketplace-asic__user-block">
                    <div className="marketplace-asic__user-profile">
                      <img src={listing.buyerAvatar} alt="Buyer" className="marketplace-asic__avatar" />
                    </div>
                    <div className="marketplace-asic__user-block-arrow">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6" fill="none">
                        <path d="M14 3L9 0.113249V5.88675L14 3ZM0 3V3.5H9.5V3V2.5H0V3Z" fill={listing.status === 'sold' ? "#FA6D6D" : "white"} fillOpacity="0.5"></path>
                      </svg>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6" fill="none">
                        <path d="M0 3L5 0.113249V5.88675L0 3ZM14 3V3.5H4.5V3V2.5H14V3Z" fill={listing.status === 'purchased' ? "#6DFA77" : "white"} fillOpacity="0.5"></path>
                      </svg>
                    </div>
                    <div className="marketplace-asic__user-profile">
                      <img src={listing.sellerAvatar} alt="Seller" className="marketplace-asic__avatar" />
                    </div>
                  </div>
                  <div className="marketplace-asic__status-block">
                    <span className={`marketplace-asic__status marketplace-asic__status--${listing.status}`}>
                      {listing.status === 'sold' ? 'Sold' : 'Purchased'}
                    </span>
                    <span className="marketplace-asic__price">{listing.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <div className="filters">
          <div className="filters__row">
            <div className="filters__row-block">
              <p className="filters__dropdown-title">ASIC model</p>
              <div className="input__dropdown filters__dropdown" ref={modelDropdownRef} onClick={toggleModelDropdown}>
                <div className="input__dropdown-content">{selectedModel}</div>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="input__dropdown-arrow" style={{ transform: modelDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                {modelDropdownOpen && (
                  <div className="input__dropdown-list">
                    <div className="filters__dropdown-list">
                      {asicModels.map((model, index) => (
                        <div
                          key={index}
                          className="filters__dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedModel(model);
                            setModelDropdownOpen(false);
                          }}
                        >
                          {model}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="filters__mobile-row-block">
              <div className="filters__row-block">
                <p className="filters__dropdown-title">Price range</p>
                <div className="input__dropdown filters__dropdown" ref={priceDropdownRef} onClick={togglePriceDropdown}>
                  <div className="input__dropdown-content">{selectedPriceRange}</div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="input__dropdown-arrow" style={{ transform: priceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                  {priceDropdownOpen && (
                    <div className="input__dropdown-list">
                      <div className="filters__dropdown-list">
                        {priceRanges.map((range, index) => (
                          <div
                            key={index}
                            className="filters__dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPriceRange(range);
                              setPriceDropdownOpen(false);
                            }}
                          >
                            {range}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="filters__tags-block">
            <div className="filters__tags">
              {filterTags.map((tag, index) => (
                <button
                  key={index}
                  className={`filters__tag ${activeFilter === tag ? 'filters__tag--active' : ''}`}
                  onClick={() => setActiveFilter(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="filters__dropdown-sort" ref={sortDropdownRef}>
              <div className="filters__dropdown-sort-value" onClick={toggleSortDropdown}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                  <path d="M12.75 3.5V15.5M12.75 15.5L9.75 12.5M12.75 15.5L15.75 12.5M5.25 15.5V3.5M5.25 3.5L2.25 6.5M5.25 3.5L8.25 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                Sort by
              </div>
              {sortDropdownOpen && (
                <div className="filters__dropdown-list">
                  <div className="filters__dropdown-item" onClick={(e) => { e.stopPropagation(); handleSortSelection('Price: Low to High'); }}>Price: Low to High</div>
                  <div className="filters__dropdown-item" onClick={(e) => { e.stopPropagation(); handleSortSelection('Price: High to Low'); }}>Price: High to Low</div>
                  <div className="filters__dropdown-item" onClick={(e) => { e.stopPropagation(); handleSortSelection('Hashrate: Low to High'); }}>Hashrate: Low to High</div>
                  <div className="filters__dropdown-item" onClick={(e) => { e.stopPropagation(); handleSortSelection('Hashrate: High to Low'); }}>Hashrate: High to Low</div>
                  <div className="filters__dropdown-item" onClick={(e) => { e.stopPropagation(); handleSortSelection('Recently Added'); }}>Recently Added</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body Section with Stats and Table */}
      <div className="used__body">
        {/* Market Stats Section */}
        <div className="market-stats used__body-stats">
          <div className="market-stats__section market-stats__section_mobile">
            <h3 className="market-stats__title">Popular Picks</h3>
            <ul className="market-stats__list market-stats__list_margin">
              {popularPicks.map((pick, index) => (
                <li 
                  key={index} 
                  className="market-stats__item"
                  onClick={() => handlePopularPickClick(pick.name)}
                  style={{ cursor: 'pointer' }}
                >
                  {pick.name} ({pick.count})
                </li>
              ))}
            </ul>
            <div className="market-stats__statistics">
              <h3 className="market-stats__title">Statistics</h3>
              <ul className="market-stats__list">
                <li className="market-stats__item">ASICs for sale <span>324</span></li>
                <li className="market-stats__item">Average price <span>$3190.43</span></li>
                <li className="market-stats__item">Sold ASICs this week <span>6</span></li>
              </ul>
            </div>
          </div>
          <p className="market-stats__banner-text">All ASICs available in the ASICs Marketplace are original devices supplied from the manufacturer, Bitmain.</p>
          <div className="market-stats__recent-deals">
            <h3 className="market-stats__title">Recent deals</h3>
            <ul className="market-stats__deals">
              {recentDeals.map((deal) => (
                <li key={deal.id} className="market-stats__deal">
                  <div className="market-stats__deal-item-block">
                    <p className="market-stats__deal-item">{deal.name}</p>
                    <p className="market-stats__deal-value">{deal.value}</p>
                  </div>
                  <p className="market-stats__deal-subitem">{deal.date} <span><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 7C8.933 7 10.5 5.433 10.5 3.5C10.5 1.567 8.933 0 7 0C5.067 0 3.5 1.567 3.5 3.5C3.5 5.433 5.067 7 7 7ZM12.25 14C12.25 11.667 9.933 9.833 7 9.833C4.067 9.833 1.75 11.667 1.75 14H12.25Z" fill="#475467"></path></svg> {deal.seller}</span></p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ASIC Table Section */}
        <div className="used__body-cards-wrapper">
          <div className="used__view-toggle">
            <button 
              className={`used__view-toggle-btn ${viewMode === 'grid' ? 'used__view-toggle-btn--active' : ''}`}
              onClick={() => handleViewToggle('grid')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"></rect>
                <rect x="11" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"></rect>
                <rect x="2" y="11" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"></rect>
                <rect x="11" y="11" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"></rect>
              </svg>
              Grid
            </button>
            <button 
              className={`used__view-toggle-btn ${viewMode === 'table' ? 'used__view-toggle-btn--active' : ''}`}
              onClick={() => handleViewToggle('table')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 3H16M2 9H16M2 15H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path>
              </svg>
              Table
            </button>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="used-asic-grid">
              {filteredAsics.map((asic) => (
                <div key={asic.id} className="used-asic-card">
                  <div className="used-asic-card__header">
                    <div className="used-asic-card__model-icon">
                      <img src={asic.modelImage} alt={asic.fullModelName} className="used-asic-card__model-image" />
                    </div>
                    <div className="used-asic-card__title-section">
                      <h3 className="used-asic-card__model-name">{asic.modelName}</h3>
                      <p className="used-asic-card__ecos-score">
                        <span className={`used-asic-card__ecos-score-value ecos-score--${asic.ecosScore.startsWith('8') ? 'green' : 'default'}`}>{asic.ecosScore}</span> FX-Pool Score
                      </p>
                    </div>
                    <div className="used-asic-card__actions-top">
                      <button 
                        className={`used-asic-card__action-btn ${likedAsics.has(asic.id) ? 'used-asic-card__action-btn--liked' : ''}`}
                        onClick={() => handleLike(asic.id)}
                        title={likedAsics.has(asic.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M10 17.5C10 17.5 2.5 12.5 2.5 7.5C2.5 4.5 4.5 2.5 7.5 2.5C9 2.5 10.5 3.25 10.5 3.25C10.5 3.25 12 2.5 13.5 2.5C16.5 2.5 18.5 4.5 18.5 7.5C18.5 12.5 10 17.5 10 17.5Z" fill={likedAsics.has(asic.id) ? "#EF4444" : "none"} stroke={likedAsics.has(asic.id) ? "#EF4444" : "currentColor"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </button>
                      <button 
                        className={`used-asic-card__action-btn ${compareAsics.has(asic.id) ? 'used-asic-card__action-btn--compared' : ''}`}
                        onClick={() => handleCompare(asic.id)}
                        title={compareAsics.has(asic.id) ? 'Remove from compare' : 'Add to compare'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path fill={compareAsics.has(asic.id) ? "#3B82F6" : "currentColor"} d="M12 3c-1.27 0-2.4.8-2.82 2H3v2h1.95L2 14c-.47 2 1 3 3.5 3s4.06-1 3.5-3L6.05 7h3.12c.33.85.98 1.5 1.83 1.83V20H2v2h20v-2h-9V8.82c.85-.32 1.5-.97 1.82-1.82h3.13L15 14c-.47 2 1 3 3.5 3s4.06-1 3.5-3l-2.95-7H21V5h-6.17C14.4 3.8 13.27 3 12 3m0 2a1 1 0 0 1 1 1a1 1 0 0 1-1 1a1 1 0 0 1-1-1a1 1 0 0 1 1-1m-6.5 5.25L7 14H4zm13 0L20 14h-3z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="used-asic-card__body">
                    <div className="used-asic-card__info-row">
                      <div className="used-asic-card__info-item">
                        <span className="used-asic-card__info-label">Hashrate</span>
                        <span className="used-asic-card__info-value">{asic.hashrate}</span>
                      </div>
                      <div className="used-asic-card__info-item">
                        <span className="used-asic-card__info-label">Power</span>
                        <span className="used-asic-card__info-value">{asic.power}</span>
                      </div>
                    </div>
                    <div className="used-asic-card__info-row">
                      <div className="used-asic-card__info-item">
                        <span className="used-asic-card__info-label">Daily Income</span>
                        <span className="used-asic-card__info-value used-asic-card__info-value--highlight">{asic.dailyIncome}/day</span>
                      </div>
                      <div className="used-asic-card__info-item">
                        <span className="used-asic-card__info-label">ROI</span>
                        <span className="used-asic-card__info-value used-asic-card__info-value--highlight">{asic.roi}/year</span>
                      </div>
                    </div>
                  </div>
                  <div className="used-asic-card__footer">
                    <div className="used-asic-card__price-section">
                      <span className="used-asic-card__price">{asic.price}</span>
                      <div className="used-asic-card__viewing">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 2.66667C4.66667 2.66667 2.33333 5.33333 1.33333 8C2.33333 10.6667 4.66667 13.3333 8 13.3333C11.3333 13.3333 13.6667 10.6667 14.6667 8C13.6667 5.33333 11.3333 2.66667 8 2.66667ZM8 11.3333C6.16 11.3333 4.66667 9.84 4.66667 8C4.66667 6.16 6.16 4.66667 8 4.66667C9.84 4.66667 11.3333 6.16 11.3333 8C11.3333 9.84 9.84 11.3333 8 11.3333ZM8 6C6.89333 6 6 6.89333 6 8C6 9.10667 6.89333 10 8 10C9.10667 10 10 9.10667 10 8C10 6.89333 9.10667 6 8 6Z" fill="currentColor"></path>
                        </svg>
                        <span>{asic.viewing} viewing</span>
                      </div>
                    </div>
                    <a 
                      href={`/marketplace/${asic.id}`} 
                      className="used-asic-card__buy-btn"
                      onClick={(e) => handleBuy(e, asic.id)}
                    >
                      Buy
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
          <div className="used-asic-table">
            <table className="used-asic-table__table">
              <thead>
                <tr>
                  <th className="used-asic-table__th used-asic-table__th--model">
                    <span>MODEL</span>
                  </th>
                  <th className="used-asic-table__th used-asic-table__th--hashrate">
                    <span className="used-asic-table__th-sortable" onClick={() => handleSort('hashrate')} style={{ cursor: 'pointer' }}>
                      HASHRATE
                      <span className="used-asic-table__th-sort-icons">
                        <svg className="used-asic-table__th-sort-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                          <path d="M12.75 3.5V15.5M12.75 15.5L9.75 12.5M12.75 15.5L15.75 12.5M5.25 15.5V3.5M5.25 3.5L2.25 6.5M5.25 3.5L8.25 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </span>
                    </span>
                  </th>
                  <th className="used-asic-table__th used-asic-table__th--daily-income">
                    <span className="used-asic-table__th-sortable" onClick={() => handleSort('income')} style={{ cursor: 'pointer' }}>
                      <span className="used-asic-table__th-text--desktop">INCOME</span>
                      <span className="used-asic-table__th-text--mobile">INCOME</span>
                      <span className="used-asic-table__th-sort-icons">
                        <svg className="used-asic-table__th-sort-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                          <path d="M12.75 3.5V15.5M12.75 15.5L9.75 12.5M12.75 15.5L15.75 12.5M5.25 15.5V3.5M5.25 3.5L2.25 6.5M5.25 3.5L8.25 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </span>
                    </span>
                  </th>
                  <th className="used-asic-table__th used-asic-table__th--roi">
                    <span className="used-asic-table__th-sortable" onClick={() => handleSort('roi')} style={{ cursor: 'pointer' }}>
                      ROI
                      <span className="used-asic-table__th-sort-icons">
                        <svg className="used-asic-table__th-sort-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                          <path d="M12.75 3.5V15.5M12.75 15.5L9.75 12.5M12.75 15.5L15.75 12.5M5.25 15.5V3.5M5.25 3.5L2.25 6.5M5.25 3.5L8.25 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </span>
                    </span>
                  </th>
                  <th className="used-asic-table__th used-asic-table__th--price">
                    <span className="used-asic-table__th-sortable" onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                      PRICE
                      <span className="used-asic-table__th-sort-icons">
                        <svg className="used-asic-table__th-sort-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                          <path d="M12.75 3.5V15.5M12.75 15.5L9.75 12.5M12.75 15.5L15.75 12.5M5.25 15.5V3.5M5.25 3.5L2.25 6.5M5.25 3.5L8.25 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAsics.map((asic) => (
                <tr key={asic.id} className="used-asic-table__row">
                  <td className="used-asic-table__td used-asic-table__td--model">
                    <div className="used-asic-table__model">
                      <div className="used-asic-table__model-icon used-asic-table__model-icon--purple">
                        <img src={asic.modelImage} alt={asic.fullModelName} className="used-asic-table__model-image" />
                      </div>
                      <div className="used-asic-table__model-info">
                        <div className="used-asic-table__model-name">{asic.modelName}</div>
                        <p className="card__ecos-score">
                          <span className={`card__ecos-score-value ecos-score--${asic.ecosScore.startsWith('8') ? 'green' : 'default'}`}>{asic.ecosScore}</span> FX-Pool Score
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="used-asic-table__td used-asic-table__td--hashrate">
                    <div className="used-asic-table__hashrate">
                      <span className="used-asic-table__hashrate-value">{asic.hashrate}</span>
                      <span className="used-asic-table__hashrate-power">{asic.power}</span>
                    </div>
                  </td>
                  <td className="used-asic-table__td used-asic-table__td--daily-income">
                    <div className="used-asic-table__daily-income">
                      <span className="used-asic-table__daily-income-value">{asic.dailyIncome}</span>
                      <span className="used-asic-table__daily-income-value-unit">/day</span>
                    </div>
                  </td>
                  <td className="used-asic-table__td used-asic-table__td--roi">
                    <div className="used-asic-table__roi">
                      <div className="used-asic-table__daily-income">
                        <span className="used-asic-table__roi-days">{asic.roi}</span>
                        <span className="used-asic-table__daily-income-value-unit">/year</span>
                      </div>
                    </div>
                  </td>
                  <td className="used-asic-table__td used-asic-table__td--price">
                    <div className="used-asic-table__price">
                      <div className="used-asic-table__price-info">
                        <span className="used-asic-table__price-value">{asic.price}</span>
                        <div className="used-asic-table__price-viewing">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 2.66667C4.66667 2.66667 2.33333 5.33333 1.33333 8C2.33333 10.6667 4.66667 13.3333 8 13.3333C11.3333 13.3333 13.6667 10.6667 14.6667 8C13.6667 5.33333 11.3333 2.66667 8 2.66667ZM8 11.3333C6.16 11.3333 4.66667 9.84 4.66667 8C4.66667 6.16 6.16 4.66667 8 4.66667C9.84 4.66667 11.3333 6.16 11.3333 8C11.3333 9.84 9.84 11.3333 8 11.3333ZM8 6C6.89333 6 6 6.89333 6 8C6 9.10667 6.89333 10 8 10C9.10667 10 10 9.10667 10 8C10 6.89333 9.10667 6 8 6Z" fill="currentColor"></path>
                          </svg>
                          <span>{asic.viewing} viewing</span>
                        </div>
                      </div>
                      <div className="used-asic-table__actions">
                        <button 
                          className={`used-asic-table__action-btn ${likedAsics.has(asic.id) ? 'used-asic-table__action-btn--liked' : ''}`}
                          onClick={() => handleLike(asic.id)}
                          title={likedAsics.has(asic.id) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 17.5C10 17.5 2.5 12.5 2.5 7.5C2.5 4.5 4.5 2.5 7.5 2.5C9 2.5 10.5 3.25 10.5 3.25C10.5 3.25 12 2.5 13.5 2.5C16.5 2.5 18.5 4.5 18.5 7.5C18.5 12.5 10 17.5 10 17.5Z" fill={likedAsics.has(asic.id) ? "#EF4444" : "none"} stroke={likedAsics.has(asic.id) ? "#EF4444" : "currentColor"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                          </svg>
                        </button>
                        <button 
                          className={`used-asic-table__action-btn ${compareAsics.has(asic.id) ? 'used-asic-table__action-btn--compared' : ''}`}
                          onClick={() => handleCompare(asic.id)}
                          title={compareAsics.has(asic.id) ? 'Remove from compare' : 'Add to compare'}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path fill={compareAsics.has(asic.id) ? "#3B82F6" : "currentColor"} d="M12 3c-1.27 0-2.4.8-2.82 2H3v2h1.95L2 14c-.47 2 1 3 3.5 3s4.06-1 3.5-3L6.05 7h3.12c.33.85.98 1.5 1.83 1.83V20H2v2h20v-2h-9V8.82c.85-.32 1.5-.97 1.82-1.82h3.13L15 14c-.47 2 1 3 3.5 3s4.06-1 3.5-3l-2.95-7H21V5h-6.17C14.4 3.8 13.27 3 12 3m0 2a1 1 0 0 1 1 1a1 1 0 0 1-1 1a1 1 0 0 1-1-1a1 1 0 0 1 1-1m-6.5 5.25L7 14H4zm13 0L20 14h-3z"></path>
                          </svg>
                        </button>
                        <a 
                          href={`/marketplace/${asic.id}`} 
                          className="used-asic-table__buy-btn"
                          onClick={(e) => handleBuy(e, asic.id)}
                        >
                          Buy
                        </a>
                      </div>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq">
        <div className="faq__title-group">
          <div className="faq__question-block">
            <h2 className="faq__question-more">Frequently Asked Questions</h2>
            <p className="faq__about-rent">How Hosted ASICs Marketplace Works</p>
          </div>
        </div>
        <div className="faq__question-group">
          {faqItems.map((item) => (
            <div key={item.id} className="faq__question-group-item">
              <div 
                className={`faq__question-title ${expandedFaq === item.id ? 'faq__question-title--active' : ''}`}
                onClick={() => toggleFaq(item.id)}
              >
                {item.question}
                <span className="faq__item-border">
                  <svg 
                    className="faq__item-sign" 
                    width="12" 
                    height="12" 
                    viewBox="0 0 12 12" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line x1="6" y1="0" x2="6" y2="12"></line>
                    <line x1="0" y1="6" x2="12" y2="6"></line>
                  </svg>
                </span>
              </div>
              {expandedFaq === item.id && (
                <div className="faq__question-description faq__question-description--expanded">
                  <p>{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;

